var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useMemo, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import Axios from 'axios';
import { produce } from 'immer';
import { useImmer } from 'use-immer';
import Cookies from 'js-cookie';
import { AsyncTools, useCallbackOnce, useMemoOnce, useAsyncState, isCapacitor as isCapacitorFunc, wrap } from '@hilma/tools';
import { AccessTokenCookieContext, AuthContext as AC, AuthFetchContext, createCookieString, GetAccessTokenContext, GetAuthItemContext, HasTwoFactorTokenContext, InterceptorsContext as IC, IsAuthenticatedContext, KlsContext, LoginContext, LoginWithCodeContext, LogoutContext, MultiSetAuthItemContext as MSAIC, RemoveAuthItemContext, SendCodeContext, SetAuthItemContext as SAIC, SuperAuthFetchContext } from '@hilma/auth-core';
export var AuthContext = AC;
export var SetAuthItemContext = SAIC;
export var MultiSetAuthItemContext = MSAIC;
export var InterceptorsContext = IC;
var AuthProvider = forwardRef(function (_a, ref) {
    var _b = _a.logoutOnUnauthorized, logoutOnUnauthorized = _b === void 0 ? false : _b, _c = _a.accessTokenCookie, at = _c === void 0 ? 'access_token' : _c, _d = _a.basename, basename = _d === void 0 ? '' : _d, _e = _a.inactivityTime, inactivityTime = _e === void 0 ? false : _e, _f = _a.twoFactorCookie, tfc = _f === void 0 ? 'two_factor_token' : _f, children = _a.children;
    var _g = useImmer({
        proxyAndHeadersId: null,
        capacitorHeadersId: null,
        logoutId: null
    }), interceptors = _g[0], updateInterceptors = _g[1];
    var accessTokenCookie = at;
    var twoFactorCookie = tfc;
    var storageKeys = useRef(['kl', 'klo', 'kloo', 'klk', accessTokenCookie, twoFactorCookie]).current;
    var isCapacitor = useMemoOnce(isCapacitorFunc);
    var _h = useAsyncState(function () {
        var storage = storageKeys.reduce(function (storage, key) {
            var _a;
            return __assign(__assign({}, storage), (_a = {}, _a[key] = isCapacitor ? localStorage.getItem(key) : Cookies.get(key), _a));
        }, {});
        return storage;
    }), storage = _h[0], setStorage = _h[1], getStorage = _h[2];
    var proxy = useRef(process.env.REACT_APP_DOMAIN || '' + basename).current;
    var kls = useMemo(function () { return ({ kl: storage.kl, klo: storage.klo }); }, [storage]);
    var getAuthItem = useCallback(function (key) { return storage[key]; }, [storage]);
    var setAuthItem = useCallback(function (key, value, setLocalStorage, setCookies, changeStorage) {
        if (setLocalStorage === void 0) { setLocalStorage = isCapacitor; }
        if (setCookies === void 0) { setCookies = !isCapacitor; }
        if (changeStorage === void 0) { changeStorage = true; }
        if (setCookies)
            Cookies.set(key, value);
        if (setLocalStorage)
            localStorage.setItem(key, value);
        if (!changeStorage)
            return getStorage();
        return setStorage(produce(function (storage) {
            storage[key] = value;
        }));
    }, [isCapacitor]);
    var multiSetAuthItem = useCallback(function (keyValuePairs, setLocalStorage, setCookies, changeStorage) {
        if (setLocalStorage === void 0) { setLocalStorage = isCapacitor; }
        if (setCookies === void 0) { setCookies = !isCapacitor; }
        if (changeStorage === void 0) { changeStorage = true; }
        return __awaiter(void 0, void 0, void 0, function () {
            var newStorage;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, Promise.all(keyValuePairs.map(function (_a) {
                            var key = _a[0], value = _a[1];
                            return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_b) {
                                return [2 /*return*/, setAuthItem(key, value, setLocalStorage, setCookies, false)];
                            }); });
                        }))];
                    case 1:
                        _a.sent();
                        if (!changeStorage)
                            return [2 /*return*/, getStorage()];
                        newStorage = {};
                        keyValuePairs.forEach(function (_a) {
                            var key = _a[0], value = _a[1];
                            newStorage[key] = value;
                        });
                        return [2 /*return*/, setStorage(function (storage) { return (__assign(__assign({}, storage), newStorage)); })];
                }
            });
        });
    }, [setAuthItem, getStorage]);
    var removeAuthItem = useCallbackOnce(function (key) {
        localStorage.removeItem(key);
        Cookies.remove(key);
        return setStorage(produce(function (storage) {
            storage[key] = null;
        }));
    });
    var getAccessToken = useCallback(function () { return getAuthItem(accessTokenCookie); }, [getAuthItem]);
    var isAuthenticated = useMemo(function () {
        var at = getAccessToken();
        return (at !== null && at !== undefined && at !== "");
    }, [getAccessToken]);
    var hasTwoFactorToken = useMemo(function () {
        var tft = getAuthItem(twoFactorCookie);
        return (tft !== null && tft !== undefined && tft !== "");
    }, [getAuthItem, twoFactorCookie]);
    var logout = useCallbackOnce(function (deleteTwoFactorToken) {
        if (deleteTwoFactorToken === void 0) { deleteTwoFactorToken = false; }
        storageKeys.forEach(function (key) { return ((deleteTwoFactorToken === true && key === twoFactorCookie) || (key !== twoFactorCookie)) && (isCapacitor ? localStorage.removeItem(key) : Cookies.remove(key)); });
        return setStorage(function (storage) {
            var _a;
            return (_a = { klo: null, kl: null, kloo: null, klk: null }, _a[accessTokenCookie] = null, _a[twoFactorCookie] = deleteTwoFactorToken ? null : storage[twoFactorCookie], _a);
        });
    });
    var authFetch = useCallback(function (input, init, fetchOffline) {
        if (init === void 0) { init = {}; }
        if (fetchOffline === void 0) { fetchOffline = false; }
        return __awaiter(void 0, void 0, void 0, function () {
            var storage_1, basicHeaders, data, error_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, getStorage()];
                    case 1:
                        storage_1 = _b.sent();
                        if (!navigator.onLine && !fetchOffline)
                            throw 'NO_INTERNET';
                        basicHeaders = !(init.body instanceof FormData) ? {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        } : {};
                        if (init.headers)
                            init.headers = __assign(__assign({}, basicHeaders), init.headers);
                        else
                            init.headers = basicHeaders;
                        if (isCapacitor)
                            init.headers = __assign(__assign({}, init.headers), { 'Authorization': storage_1[accessTokenCookie], 'Cookie': createCookieString(storage_1) });
                        return [4 /*yield*/, AsyncTools.fetch(proxy + input, init)];
                    case 2:
                        data = _b.sent();
                        return [2 /*return*/, data];
                    case 3:
                        error_1 = _b.sent();
                        if (((error_1 === null || error_1 === void 0 ? void 0 : error_1.statusCode) === 401 || ((_a = error_1 === null || error_1 === void 0 ? void 0 : error_1.error) === null || _a === void 0 ? void 0 : _a.statusCode) === 401) && logoutOnUnauthorized)
                            logout();
                        throw error_1;
                    case 4: return [2 /*return*/];
                }
            });
        });
    }, [logout, proxy, isCapacitor]);
    var superAuthFetch = useCallback(function (input, init, fetchOffline) {
        if (init === void 0) { init = {}; }
        if (fetchOffline === void 0) { fetchOffline = false; }
        return __awaiter(void 0, void 0, void 0, function () {
            var _a, error, data;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, AsyncTools.to(authFetch(input, init, fetchOffline))];
                    case 1:
                        _a = _b.sent(), error = _a[0], data = _a[1];
                        return [2 /*return*/, [data, error]];
                }
            });
        });
    }, [authFetch]);
    var login = useCallback(function (input, body, deleteTwoFactorToken) {
        if (deleteTwoFactorToken === void 0) { deleteTwoFactorToken = false; }
        return __awaiter(void 0, void 0, void 0, function () {
            var data, _a, klo_1, kl_1, kloo_1, klk_1, _b, access_token_1, id_1, error_2;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, Axios.post(input, body)];
                    case 1:
                        data = (_c.sent()).data;
                        _a = data, klo_1 = _a.klo, kl_1 = _a.kl, kloo_1 = _a.kloo, klk_1 = _a.klk, _b = accessTokenCookie, access_token_1 = _a[_b], id_1 = _a.id;
                        if (isCapacitor)
                            [
                                ['klo', klo_1 || ''],
                                ['kl', kl_1 || ''],
                                ['kloo', kloo_1 || ''],
                                ['klk', klk_1 || ''],
                                [accessTokenCookie, (access_token_1 !== null && access_token_1 !== void 0 ? access_token_1 : id_1) || '']
                            ].forEach(function (_a) {
                                var key = _a[0], value = _a[1];
                                return setAuthItem(key, value, isCapacitor, false, false);
                            });
                        if (deleteTwoFactorToken)
                            removeAuthItem(twoFactorCookie);
                        return [4 /*yield*/, setStorage(function (storage) {
                                var _a;
                                return (_a = { klo: klo_1, kl: kl_1, klk: klk_1, kloo: kloo_1 }, _a[accessTokenCookie] = access_token_1 !== null && access_token_1 !== void 0 ? access_token_1 : id_1, _a[twoFactorCookie] = deleteTwoFactorToken ? null : storage[twoFactorCookie], _a);
                            })];
                    case 2:
                        _c.sent();
                        return [2 /*return*/, { success: true, user: data }];
                    case 3:
                        error_2 = _c.sent();
                        return [2 /*return*/, { success: false, msg: error_2 }];
                    case 4: return [2 /*return*/];
                }
            });
        });
    }, [setAuthItem, isCapacitor, removeAuthItem]);
    var sendCode = useCallback(function (input, body) { return __awaiter(void 0, void 0, void 0, function () {
        var data, _a, _b, two_factor_token, error_3;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, Axios.post(input, body)];
                case 1:
                    data = (_c.sent()).data;
                    _a = data, _b = twoFactorCookie, two_factor_token = _a[_b];
                    return [4 /*yield*/, setAuthItem(twoFactorCookie, two_factor_token, false, false)];
                case 2:
                    _c.sent();
                    return [2 /*return*/, { success: true, data: data }];
                case 3:
                    error_3 = _c.sent();
                    return [2 /*return*/, { success: false, msg: error_3 }];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [setAuthItem]);
    var loginWithCode = useCallback(function (input, code) { return __awaiter(void 0, void 0, void 0, function () {
        var storage, data;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, getStorage()];
                case 1:
                    storage = _b.sent();
                    return [4 /*yield*/, login(input, (_a = { code: code }, _a[twoFactorCookie] = storage[twoFactorCookie], _a), true)];
                case 2:
                    data = _b.sent();
                    return [2 /*return*/, data];
            }
        });
    }); }, [login, getStorage]);
    useEffect(function () {
        var proxyAndHeadersId = Axios.interceptors.request.use(function (config) {
            var _a, _b;
            var basicHeaders = !(config.data instanceof FormData) ? {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            } : {};
            if (config.headers)
                config.headers = __assign(__assign({}, basicHeaders), config.headers);
            else
                config.headers = basicHeaders;
            if (((_a = config.url) === null || _a === void 0 ? void 0 : _a.indexOf("http://")) !== 0 && ((_b = config.url) === null || _b === void 0 ? void 0 : _b.indexOf("https://")) !== 0)
                config.baseURL = proxy;
            return config;
        });
        updateInterceptors(function (draft) {
            draft.proxyAndHeadersId = proxyAndHeadersId;
        });
        return function () {
            Axios.interceptors.request.eject(proxyAndHeadersId);
        };
    }, [proxy]);
    useEffect(function () {
        if (!isCapacitor)
            return;
        var capacitorHeadersId = Axios.interceptors.request.use(function (config) { return __awaiter(void 0, void 0, void 0, function () {
            var storage, basicHeaders;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getStorage()];
                    case 1:
                        storage = _a.sent();
                        basicHeaders = {
                            'Authorization': storage[accessTokenCookie],
                            'Cookie': createCookieString(storage)
                        };
                        if (config.headers)
                            config.headers = __assign(__assign({}, basicHeaders), config.headers);
                        else
                            config.headers = basicHeaders;
                        return [2 /*return*/, config];
                }
            });
        }); });
        updateInterceptors(function (draft) {
            draft.capacitorHeadersId = capacitorHeadersId;
        });
        return function () {
            Axios.interceptors.request.eject(capacitorHeadersId);
        };
    }, [isCapacitor]);
    useEffect(function () {
        var logoutId = Axios.interceptors.response.use(function (response) { return response; }, function (error) {
            var _a;
            if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && logoutOnUnauthorized)
                logout();
            return Promise.reject(error.response);
        });
        updateInterceptors(function (draft) {
            draft.logoutId = logoutId;
        });
        return function () {
            Axios.interceptors.response.eject(logoutId);
        };
    }, [logout, logoutOnUnauthorized]);
    useEffect(function () {
        if (!inactivityTime)
            return;
        var timeOunt;
        var resetTimer = function () {
            clearTimeout(timeOunt);
            timeOunt = setTimeout(logout, 10 * 60 * 1000);
        };
        resetTimer();
        var events = ['load', 'mousemove', 'mousedown', 'touchstart', 'click', 'scroll', 'mousewheel', 'DOMMouseScroll', 'keypress'];
        events.forEach(function (event) { return document.addEventListener(event, resetTimer); });
        return function () {
            events.forEach(function (event) { return document.removeEventListener(event, resetTimer); });
        };
    }, [inactivityTime]);
    var value = useMemo(function () { return ({ kls: kls, setAuthItem: setAuthItem, multiSetAuthItem: multiSetAuthItem, getAuthItem: getAuthItem, removeAuthItem: removeAuthItem, getAccessToken: getAccessToken, isAuthenticated: isAuthenticated, hasTwoFactorToken: hasTwoFactorToken, logout: logout, authFetch: authFetch, superAuthFetch: superAuthFetch, login: login, sendCode: sendCode, loginWithCode: loginWithCode, interceptors: interceptors, accessTokenCookie: accessTokenCookie }); }, [kls, setAuthItem, multiSetAuthItem, getAuthItem, removeAuthItem, getAccessToken, isAuthenticated, hasTwoFactorToken, logout, authFetch, superAuthFetch, login, sendCode, loginWithCode, interceptors, accessTokenCookie]);
    useImperativeHandle(ref, function () { return value; }, [value]);
    return wrap([AuthContext.Provider, { value: value }], [KlsContext.Provider, { value: kls }], [SetAuthItemContext.Provider, { value: setAuthItem }], [MultiSetAuthItemContext.Provider, { value: multiSetAuthItem }], [GetAuthItemContext.Provider, { value: getAuthItem }], [RemoveAuthItemContext.Provider, { value: removeAuthItem }], [GetAccessTokenContext.Provider, { value: getAccessToken }], [IsAuthenticatedContext.Provider, { value: isAuthenticated }], [HasTwoFactorTokenContext.Provider, { value: hasTwoFactorToken }], [LogoutContext.Provider, { value: logout }], [AuthFetchContext.Provider, { value: authFetch }], [SuperAuthFetchContext.Provider, { value: superAuthFetch }], [LoginContext.Provider, { value: login }], [SendCodeContext.Provider, { value: sendCode }], [LoginWithCodeContext.Provider, { value: loginWithCode }], [InterceptorsContext.Provider, { value: interceptors }], [AccessTokenCookieContext.Provider, { value: accessTokenCookie }])(children);
});
export default AuthProvider;
//# sourceMappingURL=AuthProvider.js.map